/**
 * 貼文的權限／排程標籤列（共用）。
 *
 * 三個場合共用同一份分類、文案與樣式：
 * - MyPost（建立貼文）、PostEditModal（編輯貼文）：傳入 onEdit* 後標籤即為可點擊的 chip，點了回設定畫面
 * - PostCard：自己貼文的唯讀 badge
 *
 * 分類與 VISIBILITY_OPTIONS（內容瀏覽權限）完全一致，因此一篇貼文最多只有
 * 1 個權限標籤 ＋ 1 個排程標籤（隱藏付費解鎖是獨立一類，不會拆成兩個標籤）。
 *
 * 三處都渲染成「內容上方的整行」：標籤本身不折行（Bootstrap .badge 的預設），
 * 放不下時整顆換到下一行。塞進作者列右上角那種窄欄位會擠壓暱稱與時間，
 * 且長標籤只能在膠囊內部折成兩行，因此不那樣用。
 *
 * 水平對齊由呼叫端用 className 決定（預設靠左）：
 * - MyPost（建立貼文）：靠左
 * - PostEditModal（編輯貼文）、PostCard（牆面貼文）：`justify-content-end` 靠右
 *
 * 僅供貼文使用，世界觀沒有這些設定。
 */
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import { getVisibilityOption } from '@/shared/constants/postVisibility';
import { formatDateTimeDisplay } from '@/shared/utils/formatDate';

const PostSettingBadges = ({
  visibilityType,
  unlockPrice,
  scheduledAt,
  onEditVisibility,
  onEditSchedule,
  className = '',
}) => {
  const { t } = useTranslation();

  const visibilityOption = getVisibilityOption(visibilityType);
  const badges = [];

  // 完全公開不需要標籤（沒有任何限制）
  if (visibilityOption && visibilityOption.value !== 0) {
    badges.push({
      key: 'visibility',
      icon: visibilityOption.icon,
      label: visibilityOption.needsPrice
        ? t('post_visibility_price', { title: t(visibilityOption.titleKey), price: unlockPrice })
        : t(visibilityOption.titleKey),
      onClick: onEditVisibility,
    });
  }

  if (scheduledAt) {
    badges.push({
      key: 'schedule',
      icon: faCalendarCheck,
      label: t('post_scheduled_chip', { datetime: formatDateTimeDisplay(scheduledAt) }),
      onClick: onEditSchedule,
    });
  }

  if (badges.length === 0) return null;

  return (
    <div className={`d-flex flex-wrap gap-1 ${className}`.trim()}>
      {badges.map(({ key, icon, label, onClick }) => (
        onClick ? (
          <button
            key={key}
            type="button"
            className="btn btn-sm rounded-pill border border-gray text-gray"
            onClick={onClick}
          >
            <FontAwesomeIcon icon={icon} className="me-1" />
            {label}
          </button>
        ) : (
          <span key={key} className="badge rounded-pill border border-gray text-gray fw-normal">
            <FontAwesomeIcon icon={icon} className="me-1" />
            {label}
          </span>
        )
      ))}
    </div>
  );
};

export default PostSettingBadges;
